import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  currentTheme: ThemeMode;
  isDarkMode: boolean;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'mysite-theme-preference';

const applyThemeClass = (theme: ThemeMode) => {
  // shadcn/ui uses class="dark" on <html> for dark mode
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

const isEmbeddedAppRoute = () => window.location.hash.startsWith('#/embedded-app/');

/**
 * Embedded hosts choose the theme with ?darkTheme=true|false, e.g.
 * #/embedded-app/openings?darkTheme=false. HashRouter keeps the query string in
 * the hash, so look there first and fall back to a pre-hash query string.
 * PBS Live's meeting panels send the same flag as ?darkMode=, so both names
 * are accepted (darkTheme wins when both are present).
 *
 * Embedded views default to dark, so only an explicit darkTheme=false lightens
 * them. Everywhere else an absent or unrecognised value returns null, meaning
 * "no override - use the user's stored preference".
 */
const readThemeParam = (params: URLSearchParams): string | null =>
  params.get('darkTheme') ?? params.get('darkMode');

const getQueryStringTheme = (): ThemeMode | null => {
  const hash = window.location.hash;
  const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  const value = readThemeParam(new URLSearchParams(hashQuery))
    ?? readThemeParam(new URLSearchParams(window.location.search));

  const normalized = value?.trim().toLowerCase();
  if (normalized === 'true' || normalized === '1') return 'dark';
  if (normalized === 'false' || normalized === '0') return 'light';
  return isEmbeddedAppRoute() ? 'dark' : null;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // A ?darkTheme= value in the url overrides the stored preference for this view
  const [forcedTheme, setForcedTheme] = useState<ThemeMode | null>(() => getQueryStringTheme());

  // Initialize theme from the query string, then localStorage, then default to 'light'
  // Also apply the dark class immediately to avoid flash of wrong theme
  const [currentTheme, setCurrentTheme] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    const theme: ThemeMode = forcedTheme ?? ((stored === 'dark' || stored === 'light') ? stored : 'light');
    applyThemeClass(theme);
    return theme;
  });

  const isDarkMode = currentTheme === 'dark';

  // Keep following the query string as the embedded host navigates
  useEffect(() => {
    const syncFromQueryString = () => {
      const queryTheme = getQueryStringTheme();
      setForcedTheme(queryTheme);
      if (queryTheme) setCurrentTheme(queryTheme);
    };

    window.addEventListener('hashchange', syncFromQueryString);
    return () => window.removeEventListener('hashchange', syncFromQueryString);
  }, []);

  // Persist theme preference to localStorage and apply shadcn/ui dark mode class
  useEffect(() => {
    applyThemeClass(currentTheme);

    // A url supplied theme is a per-view override, so leave the user's own preference alone
    if (forcedTheme) return;

    localStorage.setItem(THEME_STORAGE_KEY, currentTheme);

    // Dispatch storage event for cross-tab synchronization
    window.dispatchEvent(new StorageEvent('storage', {
      key: THEME_STORAGE_KEY,
      newValue: currentTheme,
      storageArea: localStorage
    }));
  }, [currentTheme, forcedTheme]);

  // Listen for theme changes from other tabs (unless the url pinned the theme)
  useEffect(() => {
    if (forcedTheme) return;

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY && e.newValue) {
        if (e.newValue === 'dark' || e.newValue === 'light') {
          setCurrentTheme(e.newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [forcedTheme]);

  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const setTheme = (theme: ThemeMode) => {
    setCurrentTheme(theme);
  };

  const value: ThemeContextType = {
    currentTheme,
    isDarkMode,
    toggleTheme,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
